import React, { useEffect, useRef } from "react";
import { Box, Typography } from "@mui/material";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const projectCards = [
  {
    id: "01",
    title: "Wolfpack Rebrand",
    category: "Brand Identity",
    year: "2024",
    accent: "#c8f65d",
    image: "https://images.unsplash.com/photo-1600508774634-4e11d34730e2?w=800&q=80",
  },
  {
    id: "02",
    title: "NovaPulse App",
    category: "UI/UX Design",
    year: "2024",
    accent: "#ff6b35",
    image: "https://images.unsplash.com/photo-1555774698-0b77e0d5fac6?w=800&q=80",
  },
  {
    id: "03",
    title: "Meridian Platform",
    category: "Design System",
    year: "2023",
    accent: "#a78bfa",
    image: "https://images.unsplash.com/photo-1551650975-87deedd944c3?w=800&q=80",
  },
  {
    id: "04",
    title: "Crest Finance",
    category: "Brand + UX",
    year: "2023",
    accent: "#38bdf8",
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80",
  },
];

// Visual states for each card relative to the active index:
// active  → rotateX: 0,   translateY: 0,     scale: 1,    opacity: 1,   zIndex: 10
// prev-1  → rotateX: -55, translateY: -210px, scale: 0.78, opacity: 0.7, zIndex: 9
// prev-2  → rotateX: -65, translateY: -340px, scale: 0.65, opacity: 0.4, zIndex: 8
// next-1  → rotateX:  55, translateY:  210px, scale: 0.78, opacity: 0.7, zIndex: 9
// next-2  → rotateX:  65, translateY:  340px, scale: 0.65, opacity: 0.4, zIndex: 8
// farther → hidden / very tilted

// function getCardState(offset) {
//   // offset = cardIndex - activeIndex
//   const abs = Math.abs(offset);
//   const sign = Math.sign(offset); // negative = above (prev), positive = below (next)

//   if (abs === 0) {
//     return { rotateX: 0, y: 0, scale: 1, opacity: 1, zIndex: 10 };
//   }
//   if (abs === 1) {
//     return {
//       rotateX: sign * -55, // prev tilts top-away, next tilts bottom-away
//       y: sign * 210,
//       scale: 0.78,
//       opacity: 0.72,
//       zIndex: 9,
//     };
//   }
//   if (abs === 2) {
//     return {
//       rotateX: sign * -65,
//       y: sign * 340,
//       scale: 0.62,
//       opacity: 0.38,
//       zIndex: 8,
//     };
//   }
//   // anything farther: hidden
//   return {
//     rotateX: sign * -80,
//     y: sign * 440,
//     scale: 0.5,
//     opacity: 0,
//     zIndex: 7,
//   };
// }

function getCardState(offset) {
  if (offset === 0) {
    return { rotateX: 0, y: 0, scale: 1, opacity: 1, zIndex: 10 };
  }
  if (offset === -1) {
    return { rotateX: -70, y: -260, scale: 0.8, opacity: 0.7, zIndex: 9 };
  }
  if (offset === -2) {
    return { rotateX: -180, y: -420, scale: 0.6, opacity: 0.4, zIndex: 8 };
  }
  if (offset === 1) {
    return { rotateX: 70, y: 260, scale: 0.8, opacity: 0.7, zIndex: 9 };
  }
  if (offset === 2) {
    return { rotateX: 240, y: 420, scale: 0.6, opacity: 0.4, zIndex: 8 };
  }

  return { opacity: 0, scale: 0.5, zIndex: 0 };
}
export default function HorizontalScrollCards() {
  const sectionRef = useRef(null);
  const cardRefs = useRef([]);
  const dotRefs = useRef([]);
  const activeIndexRef = useRef(0);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const cards = cardRefs.current;
    const dots = dotRefs.current;
    const totalCards = cards.length;
    const segmentHeight = window.innerHeight * 1.2; // scroll distance per card transition
    const totalScrollHeight = segmentHeight * (totalCards - 1);

    // ── Apply initial visual state for a given active index ──────────────────
    function applyState(activeIndex, animate = false) {
      cards.forEach((card, i) => {
        const offset = i - activeIndex;
        const state = getCardState(offset);
        const method = animate ? gsap.to : gsap.set;
        method(card, {
          rotateX: state.rotateX,
          y: state.y,
          scale: state.scale,
          opacity: state.opacity,
          zIndex: state.zIndex,
          duration: animate ? 0.6 : 0,
          ease: "power3.inOut",
          overwrite: true,
        });
      });

      // Update dots
      dots.forEach((dot, i) => {
        gsap.to(dot, {
          scale: i === activeIndex ? 1.5 : 1,
          opacity: i === activeIndex ? 1 : 0.3,
          duration: 0.3,
        });
      });

      activeIndexRef.current = activeIndex;
    }

    // ── Set initial state ─────────────────────────────────────────────────────
    gsap.set(cards, { transformPerspective: 900, transformOrigin: "50% 50%" });
    applyState(0, false);

    // ── Pin + scrub ───────────────────────────────────────────────────────────
    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: section,
        start: "top center",
        end: `+=${totalScrollHeight}`,
        pin: true,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        scrub: false,
        onUpdate: (self) => {
          // Map progress → active card index
          const rawIndex = self.progress * (totalCards - 1);
          const newIndex = Math.round(rawIndex);
          if (newIndex !== activeIndexRef.current) {
            applyState(newIndex, true);
          }
        },
      });
    }, section);

    return () => ctx.revert();
  }, []);

  return (
    <Box
      ref={sectionRef}
      sx={{
        width: "100%",
        height: "500px",
        border:'2px solid green',
        overflow: "hidden",
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        perspective: "900px",
        perspectiveOrigin: "50% 50%",
      }}
    >
      {/* Card stack — all absolutely positioned, centred */}
      <Box
        sx={{
          position: "relative",
          width: { xs: "78vw", sm: "58vw", md: "46vw", lg: "40vw" },
          height: { xs: "46vw", sm: "34vw", md: "27vw", lg: "23vw" },
          transformStyle: "preserve-3d",
        }}
      >
        {projectCards.map((card, i) => (
          <Box
            key={card.id}
            ref={(el) => (cardRefs.current[i] = el)}
            sx={{
              position: "absolute",
              inset: 0,
              borderRadius: "8px",
              overflow: "hidden",
              cursor: "pointer",
              transformStyle: "preserve-3d",
              willChange: "transform, opacity",
              boxShadow: "0 32px 72px rgba(0,0,0,0.7), 0 8px 24px rgba(0,0,0,0.5)",
            }}
          >
            {/* Image */}
            <Box
              component="img"
              src={card.image}
              alt={card.title}
              sx={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />

            {/* Gradient overlay */}
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.2) 55%, transparent 100%)",
              }}
            />

            {/* Top-left index */}
            <Typography
              sx={{
                position: "absolute",
                top: 18,
                left: 22,
                fontFamily: "'Courier New', monospace",
                fontSize: "11px",
                letterSpacing: "0.14em",
                color: card.accent,
                fontWeight: 700,
                textTransform: "uppercase",
              }}
            >
              #{card.id}
            </Typography>

            {/* Top-right year */}
            <Typography
              sx={{
                position: "absolute",
                top: 18,
                right: 22,
                fontFamily: "'Courier New', monospace",
                fontSize: "11px",
                letterSpacing: "0.1em",
                color: "rgba(255,255,255,0.4)",
              }}
            >
              {card.year}
            </Typography>

            {/* Bottom info */}
            <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0, p: "20px" }}>
              <Box
                sx={{
                  display: "inline-block",
                  px: "10px",
                  py: "3px",
                  mb: "10px",
                  border: `1px solid ${card.accent}`,
                  borderRadius: "2px",
                  background: `${card.accent}18`,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "'Courier New', monospace",
                    fontSize: "9px",
                    letterSpacing: "0.16em",
                    color: card.accent,
                    textTransform: "uppercase",
                  }}
                >
                  {card.category}
                </Typography>
              </Box>

              <Typography
                sx={{
                  fontSize: { xs: "18px", md: "22px" },
                  fontWeight: 700,
                  color: "#fff",
                  lineHeight: 1.1,
                  letterSpacing: "-0.02em",
                  mb: "10px",
                  fontFamily: "'Georgia', serif",
                }}
              >
                {card.title}
              </Typography>

              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <Box sx={{ width: "24px", height: "1px", background: card.accent }} />
                <Typography
                  sx={{
                    fontFamily: "'Courier New', monospace",
                    fontSize: "10px",
                    letterSpacing: "0.13em",
                    color: "rgba(255,255,255,0.5)",
                    textTransform: "uppercase",
                  }}
                >
                  View Case Study
                </Typography>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Progress dots — right side */}
      <Box
        sx={{
          position: "absolute",
          right: { xs: 16, md: 32 },
          top: "50%",
          transform: "translateY(-50%)",
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          alignItems: "center",
        }}
      >
        {projectCards.map((_, i) => (
          <Box
            key={i}
            ref={(el) => (dotRefs.current[i] = el)}
            sx={{
              width: "6px",
              height: "6px",
              borderRadius: "50%",
              background: "rgba(255,255,255,0.3)",
              border: "1px solid rgba(255,255,255,0.4)",
              transformOrigin: "center",
              opacity: 0.3,
            }}
          />
        ))}
      </Box>
    </Box>
  );
}